import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Pill, FlaskConical, Waves, Layers, ShieldAlert } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import ImageWithFallback from "@/components/ImageWithFallback";
import { IMAGES } from "@/lib/brand";
import { useSeo } from "@/lib/useSeo";

const NODES = [
  { key: "Gut Health", icon: Activity, desc: "Supports beneficial gut microbial balance, digestion and nutrient utilization for better feed performance." },
  { key: "Nutrition", icon: Pill, desc: "Functional vitamin and nutritional support that helps maintain overall culture performance." },
  { key: "Minerals", icon: FlaskConical, desc: "Macro & trace mineral balance to support moulting, shell formation and pond stability." },
  { key: "Water Quality", icon: Waves, desc: "Solutions that support stable, productive pond environments throughout the culture cycle." },
  { key: "Pond Bottom", icon: Layers, desc: "Organic-load and toxic-gas management for a cleaner, healthier pond bottom." },
  { key: "Stress Management", icon: ShieldAlert, desc: "Support for culture performance during challenging and changing conditions." },
];

export default function OurApproach() {
  const [active, setActive] = useState(0);
  useSeo({
    title: "Our Approach | Integrated Aquaculture Solutions | BIOSYN",
    description: "BIOSYN's integrated approach combines gut health, nutrition, minerals, water quality, pond bottom and stress management with practical technical support.",
    image: IMAGES.microscope,
  });

  return (
    <div>
      <section style={{ background: "#063B63" }} className="py-16 md:py-20">
        <div className="container-x">
          <SectionHeading light overline="Our Integrated Approach" title="One Portfolio for Every Pond Requirement"
            subtitle="We provide integrated aquaculture solutions rather than isolated products—combining product technology with practical application support for farmers, dealers and professionals." />
        </div>
      </section>

      {/* Interactive ecosystem */}
      <section className="section container-x">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative flex items-center justify-center h-[420px]">
            <div className="relative w-[340px] h-[340px] md:w-[400px] md:h-[400px]">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 md:w-44 md:h-44 rounded-full flex flex-col items-center justify-center text-center p-4 shadow-xl z-10" style={{ background: "#16B8D4" }}>
                <span className="font-heading font-extrabold text-white text-xl leading-none">BIOSYN</span>
                <span className="text-[10px] mt-1.5 font-semibold text-white/90 leading-tight">Integrated Aquaculture Solutions</span>
              </div>
              {NODES.map((n, i) => {
                const angle = (i / NODES.length) * 2 * Math.PI - Math.PI / 2;
                const r = 44;
                const x = 50 + r * Math.cos(angle);
                const y = 50 + r * Math.sin(angle);
                const isActive = active === i;
                return (
                  <button key={n.key} data-testid={`approach-node-${i}`}
                    onMouseEnter={() => setActive(i)} onClick={() => setActive(i)}
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 group"
                    style={{ left: `${x}%`, top: `${y}%` }}>
                    <span className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all duration-300 shadow-md"
                      style={{ background: isActive ? "#3FAE35" : "#FFFFFF", transform: isActive ? "scale(1.1)" : "scale(1)", border: "1px solid rgba(8,127,191,0.15)" }}>
                      <n.icon className="w-6 h-6" style={{ color: isActive ? "#fff" : "#087FBF" }} />
                    </span>
                    <span className="text-[11px] font-semibold text-center whitespace-nowrap" style={{ color: "#063B63" }}>{n.key}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="overline-label mb-2">Focus Area</p>
            <h3 className="font-heading text-3xl font-extrabold" style={{ color: "#063B63" }}>{NODES[active].key}</h3>
            <p className="mt-4 text-base leading-relaxed" style={{ color: "#4A6B82" }}>{NODES[active].desc}</p>
            <p className="mt-6 text-sm" style={{ color: "#4A6B82" }}>Hover or tap each element in the ecosystem to explore how BIOSYN addresses it.</p>
            <Link to="/products" className="btn-primary mt-7" data-testid="approach-products">See Related Products <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Solution flow */}
      <section className="section grain" style={{ background: "#FFFFFF" }}>
        <div className="container-x">
          <SectionHeading align="center" overline="Aquaculture Solution Flow" title="From Pond Condition to Practical Solution"
            subtitle="This structured process differentiates BIOSYN from companies that simply sell individual products." />
          <div className="mt-12 grid gap-3 md:grid-cols-4 lg:grid-cols-7">
            {["Pond Assessment", "Water & Pond Condition", "Culture Stage", "Identify Requirement", "Select BIOSYN Solution", "Application Guidance", "Monitor Culture Performance"].map((s, i) => (
              <div key={s} className="biosyn-card p-4 flex flex-col items-center text-center">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mb-2" style={{ background: i === 6 ? "#3FAE35" : "#087FBF" }}>{i + 1}</span>
                <p className="text-xs font-semibold leading-snug" style={{ color: "#17324D" }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden" style={{ background: "#063B63" }}>
        <ImageWithFallback src={IMAGES.lab} alt="Aquaculture laboratory research" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="container-x relative py-16 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-extrabold text-white">Integrated thinking. Practical results.</h2>
          <div className="mt-6"><Link to="/contact" className="btn-green" data-testid="approach-talk">Talk to Our Technical Team <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    </div>
  );
}
